import { useState, type FormEvent } from "react";
import { useData } from "@/contexts/DataContext";
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Video, Search } from "lucide-react";
import { Job } from "@/data/mockData";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toApiDate, toDisplayDateInput } from "@/lib/dateFormat";

const emptyJob: Omit<Job, "id"> = {
  title: "",
  company: "",
  location: "",
  category: "Finance",
  type: "Full-time",
  salary: "",
  postedDate: toDisplayDateInput(new Date().toISOString().split("T")[0]),
  applyLink: "#",
  description: "",
  contentAlignment: "justify",
  fontStyle: "times_new_roman",
  imageUrl: "",
  videoUrl: "",
};

const AdminJobs = () => {
  const { jobs, addJob, updateJob, deleteJob, jobCategories, jobLocations } = useData();
  const OTHER_CATEGORY_VALUE = "__other__";
  const presetJobCategories = Array.from(new Set(jobCategories.filter((c) => c !== "All")));
  const [editing, setEditing] = useState<Job | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<string>(emptyJob.category);
  const [customCategory, setCustomCategory] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const contentAlignmentOptions: Array<{ value: Job["contentAlignment"]; label: string }> = [
    { value: "justify", label: "Justified" },
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
  ];
  const fontStyleOptions: Array<{ value: Job["fontStyle"]; label: string }> = [
    { value: "normal", label: "Normal" },
    { value: "times_new_roman", label: "Times New Roman" },
    { value: "courier_new", label: "Courier New" },
  ];

  const openNew = () => {
    setEditing({ id: Date.now().toString(), ...emptyJob });
    setSelectedCategoryOption(emptyJob.category);
    setCustomCategory("");
    setIsNew(true);
  };
  const openEdit = (job: Job) => {
    setEditing({ ...job, postedDate: toDisplayDateInput(job.postedDate) });
    if (presetJobCategories.includes(job.category)) {
      setSelectedCategoryOption(job.category);
      setCustomCategory("");
    } else {
      setSelectedCategoryOption(OTHER_CATEGORY_VALUE);
      setCustomCategory(job.category);
    }
    setIsNew(false);
  };
  const handleSave = () => {
    if (!editing) return;
    const finalCategory =
      selectedCategoryOption === OTHER_CATEGORY_VALUE
        ? customCategory.trim()
        : selectedCategoryOption.trim();
    if (!finalCategory) {
      alert("Category is required.");
      return;
    }
    const payload = { ...editing, category: finalCategory };

    if (isNew) {
      addJob(payload);
    } else {
      updateJob(editing.id, payload);
    }

    setEditing(null);
    setCustomCategory("");
  };
  const handleDelete = (id: string) => { if (confirm("Delete this job?")) deleteJob(id); };
  const update = (field: keyof Job, value: string) => setEditing((prev) => prev ? { ...prev, [field]: value } : null);
  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const toSortableTime = (value: string) => {
    const normalized = toApiDate(value);
    const timestamp = Date.parse(normalized || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const sortedJobs = [...jobs].sort((a, b) => toSortableTime(b.postedDate) - toSortableTime(a.postedDate));
  const filteredJobs = !normalizedSearch
    ? sortedJobs
    : sortedJobs.filter((job) =>
        [
          job.title,
          job.company,
          job.location,
          job.category,
          job.type,
          job.salary,
          job.description,
          job.postedDate,
          toDisplayDateInput(job.postedDate),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedJobs = filteredJobs.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground">Manage Jobs</h1>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
          <Plus size={16} /> Add Job
        </button>
      </div>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by title, company, location, category, date..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      <div className="finance-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-semibold">Media</th>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Company</th>
                <th className="text-left px-4 py-3 font-semibold">Location</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedJobs.map((job) => (
                <tr key={job.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {job.imageUrl && (
                        <img 
                          src={job.imageUrl} 
                          alt={job.title}
                          className="w-12 h-12 object-cover rounded-lg"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                      )}
                      {job.videoUrl && (
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <Video size={16} className="text-red-600" />
                        </div>
                      )}
                      {!job.imageUrl && !job.videoUrl && (
                        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                          <ImageIcon size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium">{job.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{job.company}</td>
                  <td className="px-4 py-3 text-muted-foreground">{job.location}</td>
                  <td className="px-4 py-3"><span className="badge-success">{job.type}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(job)} className="p-1.5 rounded hover:bg-muted"><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(job.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredJobs.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No jobs found.</div>
      )}
      {filteredJobs.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredJobs.length)} of {filteredJobs.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold">{isNew ? "Add Job" : "Edit Job"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Job Title</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.title} onChange={(e) => update("title", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Company</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.company} onChange={(e) => update("company", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Location</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.location} onChange={(e) => update("location", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Category</label>
                <select
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={selectedCategoryOption}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setSelectedCategoryOption(nextValue);
                    if (nextValue === OTHER_CATEGORY_VALUE) {
                      setCustomCategory("");
                      update("category", "");
                      return;
                    }
                    update("category", nextValue);
                  }}
                >
                  {presetJobCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value={OTHER_CATEGORY_VALUE}>Others</option>
                </select>
                {selectedCategoryOption === OTHER_CATEGORY_VALUE && (
                  <input
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={customCategory}
                    onChange={(e) => {
                      const nextCustomCategory = e.target.value;
                      setCustomCategory(nextCustomCategory);
                      update("category", nextCustomCategory);
                    }}
                    placeholder="Enter category name"
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <select className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.type} onChange={(e) => update("type", e.target.value)}>
                  <option>Full-time</option><option>Part-time</option><option>Internship</option><option>Contract</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Salary</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.salary} onChange={(e) => update("salary", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Apply Link</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.applyLink} onChange={(e) => update("applyLink", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Posted Date</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={editing.postedDate}
                  onChange={(e) => update("postedDate", e.target.value)}
                  placeholder="dd-mm-yyyy"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <div className="mt-1">
                  <RichTextEditor
                    value={editing.description}
                    onChange={(value) => update("description", value)}
                    placeholder="Write rich description. Select text and apply Normal, Bold, H2, Link, or Undo."
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Content Alignment</label>
                <select
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={editing.contentAlignment}
                  onChange={(e) => update("contentAlignment", e.target.value)}
                >
                  {contentAlignmentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Font Style</label>
                <select
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  value={editing.fontStyle}
                  onChange={(e) => update("fontStyle", e.target.value)}
                >
                  {fontStyleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <ImageUpload 
                  value={editing.imageUrl} 
                  onChange={(url) => update("imageUrl", url)}
                  placeholder="Upload job image or enter URL"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Video URL</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.videoUrl} onChange={(e) => update("videoUrl", e.target.value)} placeholder="https://youtube.com/watch?v=..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm">Save</button>
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-lg bg-muted text-foreground font-semibold text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobs;

